import { useCallback, useEffect, useMemo } from 'react'
import { Button, Form, Modal, Skeleton, Spinner } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'

import {
  CheckboxGroupFieldControl,
  ComboBoxFieldControl,
  MultiSelectFieldControl,
  NumberFieldControl,
  SelectFieldControl,
  SwitchFieldControl,
  TextAreaFieldControl,
  TextFieldControl,
} from '@/components/ui/form-fields'
import { FormSection } from '@/components/ui/form-section'
import { env } from '@/config/env'
import { useBranches } from '@/features/business'
import { ImageField, ImageGalleryField } from '@/features/files'
import { errorMessage } from '@/lib/api/api-error'

import {
  useChoiceGroups,
  useOptionSets,
  useProduct,
  useProductActions,
  useProductBrands,
  useProductCategories,
} from '../hooks'
import {
  brandIdOf,
  emptyProductForm,
  marginLabel,
  optionAxisNames,
  optionCombinationCount,
  toFormValues,
  toSaveBody,
  variantsForOptions,
} from '../product-form.mapper'
import { productSchema } from '../schemas'
import type { ProductFormValues } from '../schemas'
import { ProductType, ProductUnit, translated } from '../types'

import { COST_HELP, COST_STEP, INCENTIVE_HELP, centsFormat, costFormat } from './price-format'
import { ProductChoiceGroupsField } from './product-choice-groups-field'
import { ProductInventorySection } from './product-inventory-section'
import { ProductOptionsField } from './product-options-field'
import { ProductVariantsField } from './product-variants-field'

interface ProductFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  /** The product being edited, or `null` when this is a new one. */
  productId: string | null
  onSaved: () => void
}

const typeOptions = [
  { value: ProductType.Stockable, label: 'Stockable' },
  { value: ProductType.Consumable, label: 'Consumable' },
]

/**
 * Composite is offered only for a new product: inventory sets up its recipe when
 * the product is created, and turning an existing product into one — or one back
 * — would change how its stock is counted after the fact.
 */
const newProductTypeOptions = [...typeOptions, { value: ProductType.Composite, label: 'Composite' }]

const typeDescriptions: Record<ProductType, string> = {
  [ProductType.Stockable]: 'Quantity is counted.',
  [ProductType.Consumable]: 'Made to order.',
  [ProductType.Composite]: 'Made from a recipe — selling it uses up its ingredients.',
}

const unitOptions = [
  { value: ProductUnit.Unit, label: 'Unit' },
  { value: ProductUnit.Piece, label: 'Piece' },
  { value: ProductUnit.Kilogram, label: 'Kilogram' },
]

export function ProductFormDialog({
  isOpen,
  onOpenChange,
  productId,
  onSaved,
}: ProductFormDialogProps) {
  const { product, isLoading, error: loadError } = useProduct(isOpen ? productId : null)
  const { categories } = useProductCategories()
  const { activeBrands, isLoaded: areBrandsLoaded } = useProductBrands()
  const { choiceGroups } = useChoiceGroups()
  const { optionSets } = useOptionSets()
  const { branches } = useBranches()
  const { save } = useProductActions()

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: emptyProductForm,
    mode: 'onTouched',
  })

  // The form is filled from the server's copy, not from the row that was clicked:
  // the list carries no options, no variants and no choice groups, and saving from
  // a partial picture is what would delete them.
  useEffect(() => {
    if (!isOpen) return
    if (!productId) {
      reset(emptyProductForm)
      return
    }
    if (product) reset(toFormValues(product))
  }, [isOpen, productId, product, reset])

  // A new product starts out carried at every branch: a branch is required, and the
  // common case is the whole chain, so the merchant unticks outlets rather than
  // ticking each one. Runs after the reset above so it fills the blank form, and
  // again when the branch list arrives, since it is fetched rather than given. It
  // only fills an empty selection and only fires when the dialog opens or the list
  // changes — never on a merchant's own edit — so clearing a branch stays cleared.
  // Editing is left alone: an existing product keeps the branches it was saved with.
  useEffect(() => {
    if (!isOpen || productId || branches.length === 0) return
    if (getValues('tenantBranchIds').length > 0) return

    setValue(
      'tenantBranchIds',
      branches.map((branch) => branch.id),
    )
  }, [isOpen, productId, branches, getValues, setValue])

  const options = useWatch({ control, name: 'options' })
  const variants = useWatch({ control, name: 'variants' })
  const type = useWatch({ control, name: 'type' })
  const price = useWatch({ control, name: 'price' })
  const cost = useWatch({ control, name: 'cost' })
  // When editing, only a product loaded as composite can hold this type — the
  // option is not offered to any other existing product.
  const isComposite = type === ProductType.Composite

  const axes = useMemo(() => optionAxisNames(options ?? []), [options])
  const combinationCount = useMemo(() => optionCombinationCount(options ?? []), [options])

  // The variants array is owned here rather than in the section that renders it:
  // filling the grid swaps every row at once, and `replace` on the one field array
  // that owns the list is the only supported way to do that.
  const {
    fields: variantFields,
    append: appendVariant,
    remove: removeVariant,
    replace: replaceVariants,
  } = useFieldArray({ control, name: 'variants' })

  /** Builds the rows the options imply, keeping what has already been filled in. */
  const fillVariants = useCallback(
    (from: ProductFormValues['options']) => {
      const rows = variantsForOptions(from, getValues('variants') ?? [], {
        sku: getValues('sku'),
        price: getValues('price'),
        cost: getValues('cost'),
        incentive: getValues('incentive'),
      })

      // Nothing to build from — an axis with no values yet, most likely. Leaving
      // the grid alone beats clearing rows the merchant has already typed into.
      if (rows.length > 0) replaceVariants(rows)
    },
    [getValues, replaceVariants],
  )

  function addVariant() {
    appendVariant({
      sku: '',
      gtin: '',
      price: getValues('price') ?? 0,
      // Seeded like the price, and blank when the product has none — never zero.
      cost: getValues('cost'),
      incentive: getValues('incentive'),
      thumbnail: null,
      attributes: axes.map((name) => ({ name, value: '' })),
    })
  }

  // Renaming or removing an axis has to move through every variant, or the server
  // rejects the save — it checks each variant's attributes against the option list.
  // Values are carried across by name so editing an axis does not blank the grid.
  useEffect(() => {
    ;(variants ?? []).forEach((variant, index) => {
      const current = variant.attributes ?? []
      const isAligned =
        current.length === axes.length && current.every((item, i) => item.name === axes[i])

      if (isAligned) return

      setValue(
        `variants.${index}.attributes`,
        axes.map((name) => ({
          name,
          value: current.find((item) => item.name === name)?.value ?? '',
        })),
      )
    })
  }, [axes, variants, setValue])

  /**
   * The active brands, plus the one this product was saved with if it has since
   * been deactivated. Without it the field would open empty on such a product —
   * reading as "no brand" — and the merchant could not tell what it carries.
   * It is offered on this product's form alone, so clearing it by mistake can be
   * undone before saving; no other product can pick it up.
   */
  const savedBrand = product?.brand ?? null
  const brandOptions = useMemo(() => {
    const options = activeBrands.map((brand) => ({
      value: brand.id,
      label: translated(brand.name),
    }))

    if (savedBrand && !options.some((option) => option.value === savedBrand.id)) {
      // Missing from a list that has not arrived, or failed to, says nothing
      // about the brand — it is only called inactive once the list is in.
      const isInactive = savedBrand.isActive === false || areBrandsLoaded
      const name = translated(savedBrand.name)

      options.unshift({ value: savedBrand.id, label: isInactive ? `${name} (inactive)` : name })
    }

    return options
  }, [activeBrands, savedBrand, areBrandsLoaded])

  const submit = handleSubmit(async (values) => {
    // The brand the product opened with is what tells a cleared field from an
    // empty one — only the first sends `brandId: null`.
    await save.mutateAsync(toSaveBody(values, brandIdOf(product)))
    onSaved()
  })

  const isBusy = save.isPending
  const isEditing = Boolean(productId)
  const variantCount = variantFields.length
  const hasVariants = variantCount > 0

  // Once there are variants the product's own price is a placeholder the server
  // replaces, so a margin against it would describe nothing that sells.
  const priceNote =
    variantCount > 1
      ? 'Overridden by the cheapest variant once saved.'
      : hasVariants
        ? undefined
        : marginLabel(price, cost)

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Backdrop className="overlay-scope" variant="blur">
        {/* The size variants top out at `max-w-lg`, which is a third of what a
            two-column form needs, so the width is set on the dialog itself.
            `scroll="inside"` caps the dialog at the viewport and gives the body its
            own scrollbar, keeping Save in place while the fields above it move. */}
        <Modal.Container size="lg" scroll="inside" placement="center">
          <Modal.Dialog className="max-w-5xl">
            <Modal.CloseTrigger className="end-3 top-3 size-10" />

            <Form
              onSubmit={submit}
              validationBehavior="aria"
              // `flex-1 min-h-0` completes the chain the capped dialog needs: the
              // form fills it, the body shrinks inside the form, and the scrollbar
              // lands on the body rather than the content being clipped.
              className="flex min-h-0 flex-1 flex-col"
            >
              <Modal.Header className="gap-1 pe-10">
                <Modal.Heading>{isEditing ? 'Edit product' : 'New product'}</Modal.Heading>
                {/* Editing names the product being edited; creating says what the
                    form covers. Neither line appears while the product is still on
                    its way — a subtitle that swaps mid-load reads as a glitch. */}
                <p className="text-muted min-h-4 text-xs leading-relaxed">
                  {isEditing
                    ? product &&
                      `${translated(product.name)}${
                        variantCount > 1 ? ` · ${variantCount} variants` : ''
                      }`
                    : 'The product itself, its picture, its options and its add-ons.'}
                </p>
              </Modal.Header>

              <Modal.Body>
                {/* A product that would not load must say so: the form falls back to
                    blank fields, and saving those would create a second product
                    rather than edit the one that was clicked. */}
                {loadError && (
                  <p className="text-danger mb-4 text-xs" role="alert">
                    {errorMessage(loadError, 'Could not load this product. Please try again.')}
                  </p>
                )}

                {isLoading ? (
                  <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_19rem]">
                    <Skeleton className="h-96 w-full rounded-2xl" />
                    <Skeleton className="hidden h-64 w-full rounded-2xl lg:block" />
                  </div>
                ) : (
                  /* Two columns from `lg` up: what the product *is* runs down the
                     main column in the order it is filled in, while the things that
                     merely file it — picture, visibility, categories — sit aside so
                     they stop interrupting that run. Below `lg` the two stack, main
                     column first, which is also the tab order in both layouts. */
                  <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_19rem]">
                    <div className="flex min-w-0 flex-col gap-4">
                      <FormSection title="Details">
                        <div className="flex flex-col gap-4">
                          <div className="grid gap-4 sm:grid-cols-2">
                            <TextFieldControl
                              control={control}
                              name="nameEn"
                              label="Name (English)"
                              placeholder="Iced latte"
                              isRequired
                              autoFocus
                              isDisabled={isBusy}
                            />
                            <TextFieldControl
                              control={control}
                              name="nameKm"
                              label="Name (Khmer)"
                              placeholder="Optional"
                              isDisabled={isBusy}
                            />
                          </div>

                          {/* Both halves of the description, like both halves of
                              the name: the API takes it as a `TranslationDto` and
                              the mapper has always sent `km` — it just had no box
                              to come from, so a Khmer description could be loaded
                              and saved back but never written or corrected. */}
                          <div className="grid gap-4 sm:grid-cols-2">
                            <TextAreaFieldControl
                              control={control}
                              name="descriptionEn"
                              label="Description (English)"
                              placeholder="What the customer is buying."
                              rows={3}
                              isDisabled={isBusy}
                            />
                            <TextAreaFieldControl
                              control={control}
                              name="descriptionKm"
                              label="Description (Khmer)"
                              placeholder="Optional"
                              rows={3}
                              isDisabled={isBusy}
                            />
                          </div>
                        </div>
                      </FormSection>

                      <FormSection
                        title="Pricing & identity"
                        description="What it sells for, and how the till and the scanner find it."
                      >
                        {/* The money leads the grid, cost and incentive beside the
                            price they are read against. With variants they move
                            into each variant's price editor instead — every
                            variant has its own, and two boxes here would only
                            look like a figure the variants share. */}
                        <div className="grid gap-4 sm:grid-cols-3">
                          <NumberFieldControl
                            control={control}
                            name="price"
                            label="Price"
                            minValue={0}
                            step={0.01}
                            formatOptions={centsFormat}
                            description={priceNote}
                            isRequired
                            isDisabled={isBusy}
                          />
                          {!hasVariants && (
                            <>
                              <NumberFieldControl
                                control={control}
                                name="cost"
                                label="Cost"
                                placeholder="Not set"
                                minValue={0}
                                step={COST_STEP}
                                formatOptions={costFormat}
                                description={COST_HELP}
                                isDisabled={isBusy}
                              />
                              <NumberFieldControl
                                control={control}
                                name="incentive"
                                label="Incentive"
                                placeholder="Not set"
                                minValue={0}
                                step={0.01}
                                formatOptions={centsFormat}
                                description={INCENTIVE_HELP}
                                isDisabled={isBusy}
                              />
                            </>
                          )}
                          <SelectFieldControl
                            control={control}
                            name="type"
                            label="Type"
                            options={
                              // An existing composite keeps its type: the list
                              // still names it, but it cannot be changed.
                              productId === null || isComposite
                                ? newProductTypeOptions
                                : typeOptions
                            }
                            description={
                              productId !== null && isComposite
                                ? 'A composite product keeps its type once created.'
                                : typeDescriptions[type]
                            }
                            isRequired
                            isDisabled={isBusy || (productId !== null && isComposite)}
                          />
                          <SelectFieldControl
                            control={control}
                            name="unit"
                            label="Unit"
                            options={unitOptions}
                            placeholder="Not set"
                            isDisabled={isBusy}
                          />
                          <TextFieldControl
                            control={control}
                            name="sku"
                            label="SKU"
                            placeholder="Optional"
                            isDisabled={isBusy}
                          />
                          <TextFieldControl
                            control={control}
                            name="gtin"
                            label="Barcode"
                            placeholder="Optional"
                            isDisabled={isBusy}
                          />
                        </div>
                      </FormSection>

                      <ProductOptionsField
                        control={control}
                        optionSets={optionSets}
                        onOptionSetApplied={fillVariants}
                        isDisabled={isBusy}
                      />

                      <ProductVariantsField
                        control={control}
                        fields={variantFields}
                        onAdd={addVariant}
                        onRemove={removeVariant}
                        onFill={
                          combinationCount > 0
                            ? () => fillVariants(getValues('options'))
                            : undefined
                        }
                        axes={axes}
                        combinationCount={combinationCount}
                        errors={errors}
                        isDisabled={isBusy}
                      />

                      <ProductChoiceGroupsField
                        control={control}
                        choiceGroups={choiceGroups}
                        isDisabled={isBusy}
                      />
                    </div>

                    <aside className="flex flex-col gap-4">
                      <FormSection
                        title="Images"
                        description="The thumbnail and the cover images shown for this product."
                      >
                        {/* Uploaded the moment they are picked, so what the form
                            holds is the stored file — the id it saves with, and the
                            url that keeps the preview up after the picker closes. */}
                        <div className="flex flex-col gap-4">
                          <Controller
                            control={control}
                            name="thumbnail"
                            render={({ field }) => (
                              <ImageField
                                label="Thumbnail"
                                description="Shown on the till and the storefront."
                                value={field.value}
                                onChange={field.onChange}
                                aspect="square"
                                isDisabled={isBusy}
                              />
                            )}
                          />

                          <Controller
                            control={control}
                            name="bannerImages"
                            render={({ field }) => (
                              <ImageGalleryField
                                label="Cover images"
                                description="Shown on the product page. Add as many as you like, up to 8."
                                value={field.value}
                                onChange={field.onChange}
                                isDisabled={isBusy}
                              />
                            )}
                          />
                        </div>
                      </FormSection>

                      {/* Only a saved product can be missing from inventory, and
                          only where inventory has a host to ask. */}
                      {product && env.inventoryApiBaseUrl && (
                        <ProductInventorySection product={product} isDisabled={isBusy} />
                      )}

                      <FormSection title="Status">
                        <SwitchFieldControl
                          control={control}
                          name="published"
                          label="Published"
                          description="Unpublished products stay out of the storefront."
                          isDisabled={isBusy}
                        />
                      </FormSection>

                      <FormSection
                        title="Branches"
                        description="Required — the outlets that carry this product."
                      >
                        <MultiSelectFieldControl
                          control={control}
                          name="tenantBranchIds"
                          label="Branches"
                          placeholder={
                            branches.length === 0 ? 'No branches yet' : 'Select branches'
                          }
                          options={branches.map((branch) => ({
                            value: branch.id,
                            label: translated(branch.name, 'Branch'),
                          }))}
                          isLabelHidden
                          isRequired
                          isDisabled={isBusy || branches.length === 0}
                        />
                      </FormSection>

                      <FormSection
                        title="Brand"
                        description="Optional. Its variants carry the same brand."
                      >
                        <ComboBoxFieldControl
                          control={control}
                          name="brandId"
                          label="Brand"
                          placeholder={
                            brandOptions.length === 0 ? 'No brands yet' : 'Search brands'
                          }
                          emptyMessage="No active brand by that name."
                          options={brandOptions}
                          isLabelHidden
                          isDisabled={isBusy || brandOptions.length === 0}
                        />
                      </FormSection>

                      <FormSection
                        title="Categories"
                        description="Where the storefront files this product."
                      >
                        <CheckboxGroupFieldControl
                          control={control}
                          name="categoryIds"
                          label="Categories"
                          options={categories.map((category) => ({
                            value: category.id,
                            label: translated(category.name),
                          }))}
                          emptyMessage="No categories yet. Create one before filing products under it."
                          isLabelHidden
                          isDisabled={isBusy}
                        />
                      </FormSection>
                    </aside>
                  </div>
                )}

                {save.error && (
                  <p className="text-danger mt-4 text-xs" role="alert">
                    {errorMessage(save.error, 'Could not save this product. Please try again.')}
                  </p>
                )}
              </Modal.Body>

              <Modal.Footer>
                <Button variant="ghost" onPress={() => onOpenChange(false)} isDisabled={isBusy}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" isDisabled={isBusy || isLoading}>
                  {isBusy && <Spinner size="sm" />}
                  {isBusy ? 'Saving…' : isEditing ? 'Save changes' : 'Create product'}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  )
}
